import { useState, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { doc, updateDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { db } from '../config/firebase';
import { UserCircleIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { Organization, User, Role } from '@easydone/shared';

export function ProfilePage() {
  const { currentUser, userData, refreshUserData } = useAuth();
  const { currentOrg } = useOrg();
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState(userData?.displayName || '');
  const [phoneNumber, setPhoneNumber] = useState(userData?.phoneNumber || '');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!currentUser || !userData) return;

    setLoading(true);
    setMessage(null);

    try {
      // Update Firebase Auth profile
      await updateProfile(currentUser, { displayName });

      // Update Firestore user document
      const userRef = doc(db, 'users', userData.id);
      await updateDoc(userRef, {
        displayName,
        phoneNumber,
        updatedAt: serverTimestamp(),
      });

      // Refresh user data in context
      await refreshUserData();

      setMessage({ type: 'success', text: 'Profile updated successfully' });
      setEditing(false);
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setDisplayName(userData?.displayName || '');
    setPhoneNumber(userData?.phoneNumber || '');
    setEditing(false);
    setMessage(null);
  };

  const handleFixAccount = async () => {
    if (!currentUser) return;

    const orgName = prompt('Enter your organization name:');
    if (!orgName) return;

    setLoading(true);
    setMessage(null);

    try {
      // Create organization
      const orgId = currentUser.uid;
      const organization: Organization = {
        id: orgId,
        name: orgName,
        tier: 'starter',
        billingEmail: currentUser.email || '',
        subscriptionStatus: 'trial',
        trialEndsAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        settings: {
          branding: {
            primaryColor: '#3B82F6',
            companyName: orgName,
          },
          features: {
            ocrEnabled: false,
            apiAccessEnabled: false,
            ssoEnabled: false,
            customIntegrations: false,
          },
          limits: {
            maxUsers: 10,
            maxSites: 50,
            maxStorageGB: 10,
            ocrCreditsPerMonth: 100,
          },
          notifications: {
            inspectionCompleted: true,
            inspectionOverdue: true,
            calibrationDue: true,
          },
        },
        createdAt: serverTimestamp() as any,
        createdBy: currentUser.uid,
        updatedAt: serverTimestamp() as any,
        industry: 'water_utility',
      };

      await setDoc(doc(db, 'organizations', orgId), organization);

      // Create user profile
      const userProfile: User = {
        id: currentUser.uid,
        email: currentUser.email || '',
        displayName: currentUser.displayName || orgName,
        orgId: orgId,
        role: Role.ORG_ADMIN,
        permissions: ['*'],
        status: 'active',
        preferences: {
          language: 'en',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          notifications: {
            email: true,
            push: true,
          },
        },
        createdAt: serverTimestamp() as any,
      };

      await setDoc(doc(db, 'users', currentUser.uid), userProfile);

      // Refresh data
      await refreshUserData();

      setMessage({ type: 'success', text: 'Account fixed successfully! Please refresh the page.' });

      // Reload page after 2 seconds
      setTimeout(() => window.location.reload(), 2000);
    } catch (error: any) {
      console.error('Failed to fix account:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to fix account' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
        <p className="text-gray-600 mt-1">Manage your personal information</p>
      </div>

      {message && (
        <div
          className={`rounded-md p-4 mb-6 ${
            message.type === 'success' ? 'bg-green-50' : 'bg-red-50'
          }`}
        >
          <p
            className={`text-sm ${
              message.type === 'success' ? 'text-green-800' : 'text-red-800'
            }`}
          >
            {message.text}
          </p>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        {/* Profile Picture */}
        <div className="flex items-center mb-6 pb-6 border-b border-gray-200">
          <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center mr-4">
            {currentUser?.photoURL ? (
              <img
                src={currentUser.photoURL}
                alt="Profile"
                className="w-20 h-20 rounded-full"
              />
            ) : (
              <UserCircleIcon className="w-12 h-12 text-primary-600" />
            )}
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {userData?.displayName || 'No name set'}
            </h2>
            <p className="text-gray-600">{currentUser?.email}</p>
            {userData?.role && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 mt-2">
                {userData.role.replace('_', ' ')}
              </span>
            )}
          </div>
        </div>

        {/* Organization Info */}
        <div className="mb-6 pb-6 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Organization</h3>
          <p className="text-gray-900">{currentOrg?.name || 'No organization'}</p>
          <p className="text-sm text-gray-600 mt-1">
            Tier: <span className="capitalize">{currentOrg?.tier}</span>
          </p>
        </div>

        {/* Profile Form */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Name
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={!editing}
                className="input w-full disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="Enter your name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={currentUser?.email || ''}
                disabled
                className="input w-full bg-gray-50 text-gray-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Email cannot be changed
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number (Optional)
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                disabled={!editing}
                className="input w-full disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="Enter your phone number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User ID
              </label>
              <input
                type="text"
                value={userData?.id || ''}
                disabled
                className="input w-full bg-gray-50 text-gray-500 font-mono text-xs"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            {editing ? (
              <>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="btn btn-primary"
              >
                Edit Profile
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Debug Info */}
      <div className="bg-gray-900 text-gray-100 rounded-lg p-6 mt-6 font-mono text-xs">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">Debug Information</h3>
          {!userData && (
            <button
              onClick={handleFixAccount}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
            >
              <WrenchScrewdriverIcon className="w-5 h-5" />
              Fix Account
            </button>
          )}
        </div>
        <div className="space-y-4">
          <div>
            <div className="text-gray-400 mb-1">Current User (Firebase Auth):</div>
            <pre className="bg-gray-800 p-3 rounded overflow-auto">
              {JSON.stringify({
                uid: currentUser?.uid,
                email: currentUser?.email,
                displayName: currentUser?.displayName,
              }, null, 2)}
            </pre>
          </div>
          <div>
            <div className="text-gray-400 mb-1">User Data (Firestore):</div>
            <pre className="bg-gray-800 p-3 rounded overflow-auto">
              {JSON.stringify(userData, null, 2)}
            </pre>
          </div>
          <div>
            <div className="text-gray-400 mb-1">Current Organization:</div>
            <pre className="bg-gray-800 p-3 rounded overflow-auto">
              {JSON.stringify(currentOrg, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
